import { Alert } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ContentWrapper } from '@/components/ContentWrapper';
import { PrimaryButton } from '@/components/shared/Button';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { usePaymentsMutations } from '@/services/paymentsMutations';

// The return target is hardcoded on purpose: an attacker-controlled redirect
// must never be read from a query parameter (open-redirect protection).
const MONITORING_PAGE_ROUTE = '/transaction-monitoring/page/0';

/**
 * Operator callback for the house-account Aiia connect / re-authorize flow.
 * Reads code/consentId/state from the redirect, exchanges them, then returns to
 * the monitoring page. The sensitive params are stripped from the URL
 * immediately and are never persisted client-side.
 */
export const HouseAccountAiiaConnectCallbackPage = () => {
    const { t } = useTranslation();
    const [error, setError] = useState<string>();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { exchangeHouseAccountAiia } = usePaymentsMutations();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // The redirect fires once; guard against a re-run replaying the same code.
    const hasExchangedRef = useRef(false);

    useEffect(() => {
        if (hasExchangedRef.current) {
            return;
        }
        hasExchangedRef.current = true;

        const code = searchParams.get('code') ?? '';
        const consentId = searchParams.get('consentId') ?? '';
        const state = searchParams.get('state') ?? '';

        // Strip code/state/consentId from the address bar so they are not left in
        // browser history.
        window.history.replaceState({}, '', window.location.pathname);

        if (!code || !state) {
            setError(t('transaction_monitoring.connect_failed_or_cancelled'));
            return;
        }

        // searchParams.get already URL-decodes the values, so they are passed
        // through as-is (no second decode).
        exchangeHouseAccountAiia.mutate(
            {
                code,
                consentId,
                state
            },
            {
                onSuccess: () => {
                    setAlert(
                        t('transaction_monitoring.connect_success'),
                        'success'
                    );
                    navigate(MONITORING_PAGE_ROUTE);
                },
                onError: (mutationError) =>
                    backendErrorHandler('', () => {
                        setError(
                            t(
                                'transaction_monitoring.connect_failed_or_cancelled'
                            )
                        );
                    })(mutationError)
            }
        );
    }, [searchParams]);

    return (
        <>
            {error && (
                <ContentWrapper>
                    <Box textAlign="center">
                        <Alert severity="error">{error}</Alert>
                        <PrimaryButton
                            style={{ marginTop: '1rem' }}
                            onClick={() => navigate(MONITORING_PAGE_ROUTE)}
                        >
                            {t('transaction_monitoring.page_title')}
                        </PrimaryButton>
                    </Box>
                </ContentWrapper>
            )}
        </>
    );
};

export default HouseAccountAiiaConnectCallbackPage;
